import React, { useRef, useEffect, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { cn } from '../../lib/utils';

interface Props {
  onSelect: (isoDate: string) => void;
  onClose: () => void;
}

const PRESETS = [
  { label: '7 days', days: 7 },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
];

function addDays(d: number): string {
  const dt = new Date();
  dt.setDate(dt.getDate() + d);
  return dt.toISOString();
}

export function SnoozeMenu({ onSelect, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [customDate, setCustomDate] = useState('');

  // Close on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const handleCustomSubmit = () => {
    if (!customDate) return;
    onSelect(new Date(customDate).toISOString());
  };

  return (
    <div
      ref={ref}
      className="absolute bottom-full left-0 mb-1 z-50 w-52 rounded-xl bg-white dark:bg-zinc-900 border border-border shadow-lg py-2 animate-in fade-in slide-in-from-bottom-1 duration-150"
    >
      <p className="px-3 pb-1.5 text-xs font-semibold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
        Remind me in
      </p>
      {PRESETS.map((p) => (
        <button
          key={p.days}
          onClick={() => onSelect(addDays(p.days))}
          className={cn(
            'w-full text-left px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300',
            'hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors',
          )}
        >
          {p.label}
        </button>
      ))}
      <div className="mx-3 my-1.5 h-px bg-border" />
      <div className="px-3 py-1.5 space-y-1.5">
        <p className="text-xs text-zinc-400 dark:text-zinc-500 flex items-center gap-1">
          <CalendarDays className="h-3.5 w-3.5" />
          Custom date
        </p>
        <input
          type="date"
          value={customDate}
          min={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setCustomDate(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs text-zinc-900 dark:text-zinc-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <button
          onClick={handleCustomSubmit}
          disabled={!customDate}
          className="w-full rounded-md bg-zinc-100 dark:bg-zinc-800 py-1 text-xs font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Set custom date
        </button>
      </div>
    </div>
  );
}
